import * as React from 'react';
import { NodeViewWrapper, NodeViewProps } from '@tiptap/react';
import { Badge } from '@cotera/client/v0/components/ui/badge';
import { AlertTriangle, Link2 } from 'lucide-react';
import { Tooltip } from '@cotera/client/app/components/ui/tooltip';
import { useQuery } from '@tanstack/react-query';
import { coco as apiCoco, ResourceType } from '@cotera/api';
import { resourceViewOf } from '@cotera/sdk/core';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { Assert } from '@cotera/utilities';
import { snakeCase } from 'lodash';
import {
  lookupMentionedTool,
  useAgentMentionToolsMode,
} from '@cotera/client/app/v1/agent/agent-mention-tools-context';
import {
  getResourceBadgeColorFromMentionData,
  resourceColorBadgeVariant,
} from '@cotera/client/app/v1/resources/type/resource-type-colors';

type MentionData = {
  t: string;
  name: string;
  data?: unknown;
};

type MentionedResource = {
  resourceType:
    | ResourceType.Agent
    | ResourceType.Document
    | ResourceType.Dataset;
  resourceId: string;
};

type MentionColorVariant =
  | 'primary'
  | 'blue'
  | 'green'
  | 'orange'
  | 'amber'
  | 'purple'
  | 'rose'
  | 'slate'
  | 'violet'
  | 'teal'
  | 'brown';

const isWorkflowToolMention = (data: MentionData | undefined): boolean => {
  if (data?.t !== 'tool') {
    return false;
  }
  const inner = data.data;
  if (inner === undefined || inner === null || typeof inner !== 'object') {
    return false;
  }
  const toolId = Reflect.get(inner, 'toolId');
  const toolName = Reflect.get(inner, 'toolName');
  return (
    (typeof toolId === 'string' && apiCoco.isWorkflowToolKey(toolId)) ||
    (typeof toolName === 'string' && apiCoco.isWorkflowToolKey(toolName))
  );
};

/**
 * Derive badge color from mention type. Used for coco-chat, agent, and document
 * editors — type-based so pills stay consistent even when attrs.color is missing
 * or stamped with the old extension default (`blue`).
 *
 * Keep this in sync with `getColorFromMentionType` in mention-dropdown-menu.tsx so
 * the menu dot always matches the pill it inserts. One color per category, distinct
 * across categories: tools=tool resource color, documents=blue, agents=violet,
 * columns=amber.
 */
const getMentionColorFromType = (
  data: MentionData | undefined
): MentionColorVariant => {
  const t = data?.t;
  if (t === 'tool') {
    return isWorkflowToolMention(data)
      ? resourceColorBadgeVariant('workflow')
      : resourceColorBadgeVariant('tool');
  }
  if (t === 'document') {
    return 'blue';
  }
  if (t === 'expr') {
    return 'green';
  }
  if (t === 'agent') {
    return 'violet';
  }
  if (t === 'column') {
    return 'amber';
  }
  if (t === 'resource') {
    return 'primary';
  }
  if (t === 'input') {
    return 'green';
  }
  return 'purple';
};

const getMentionBadgeColor = (
  data: MentionData | undefined
): MentionColorVariant => {
  const resourceBadgeColor = getResourceBadgeColorFromMentionData(data);
  if (resourceBadgeColor !== undefined) {
    return resourceBadgeColor;
  }
  return getMentionColorFromType(data);
};

/**
 * TipTap may store `attrs.data` as a JSON string (insert / HTML parse) or as an
 * already-parsed object (server jsonb / setContent). Accept both so badge color
 * and click handlers resolve the same way on agent and document editors.
 */
const parseMentionDataAttr = (raw: unknown): MentionData | undefined => {
  if (raw === undefined || raw === null || raw === '') {
    return undefined;
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    const record = raw as Record<string, unknown>;
    const t = record['t'];
    const name = record['name'];
    if (typeof t !== 'string') {
      return undefined;
    }
    return {
      t,
      name: typeof name === 'string' ? name : '',
      data: record['data'],
    };
  }
  if (typeof raw !== 'string') {
    return undefined;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      parsed === null ||
      typeof parsed !== 'object' ||
      Array.isArray(parsed)
    ) {
      return undefined;
    }
    return parseMentionDataAttr(parsed);
  } catch {
    return undefined;
  }
};

/**
 * Hook to fetch tool info by key.
 * Uses useQuery (non-suspense) to properly subscribe to cache updates when credentials change.
 */
const useToolInfo = (toolKey: string) => {
  const { orgId, client } = useWhoami((s) => ({
    orgId: s.org.id,
    client: s.api.t,
  }));

  const { data: tools } = useQuery({
    queryKey: queryKeys.toolsV2.all({ orgId }),
    queryFn: async () => {
      return Assert.assertOk(await client.toolsV2.list());
    },
  });

  return (
    tools?.find((tool) => snakeCase(tool.key) === snakeCase(toolKey)) ?? null
  );
};

/**
 * Component that checks tool configuration status and shows warning indicator.
 */
const ToolConfigurationIndicator: React.FC<{ toolKey: string }> = ({
  toolKey,
}) => {
  const mentionMode = useAgentMentionToolsMode();
  const toolInfo = useToolInfo(toolKey);
  if (mentionMode.mode === 'owner_org') {
    if (mentionMode.isLoading) {
      return null;
    }
    const row = lookupMentionedTool(mentionMode.toolLookup, toolKey);
    if (row !== null && !row.canExecute) {
      return (
        <Tooltip
          side="top"
          tooltipContent={
            row.notExecutableReason ?? 'This tool needs configuration'
          }
          openDelay={300}
        >
          <AlertTriangle className="w-3 h-3 mr-1 text-amber-500" />
        </Tooltip>
      );
    }
    return null;
  }

  const needsConfiguration = toolInfo !== null && !toolInfo.canExecute;

  if (!needsConfiguration) {
    return null;
  }

  return (
    <Tooltip
      side="top"
      tooltipContent={
        toolInfo.notExecutableReason ?? 'This tool needs configuration'
      }
      openDelay={300}
    >
      <AlertTriangle className="w-3 h-3 mr-1 text-amber-500" />
    </Tooltip>
  );
};

const OwnerOrgToolIndicator: React.FC<{ toolKey: string }> = ({ toolKey }) => {
  const mentionMode = useAgentMentionToolsMode();
  if (mentionMode.mode !== 'owner_org' || !mentionMode.toolsRunInOwnerOrg) {
    return null;
  }
  if (mentionMode.isLoading) {
    return null;
  }
  if (lookupMentionedTool(mentionMode.toolLookup, toolKey) === null) {
    return null;
  }
  return (
    <Tooltip
      side="top"
      tooltipContent="This tool runs using credentials from the agent owner's organization."
      openDelay={300}
    >
      <Link2
        className="w-3 h-3 mr-0.5 text-muted-foreground shrink-0"
        aria-hidden
      />
    </Tooltip>
  );
};

const MentionOwnershipIndicator: React.FC<{
  tooltipContent: React.ReactNode;
}> = ({ tooltipContent }) => {
  return (
    <Tooltip side="top" tooltipContent={tooltipContent} openDelay={300}>
      <Link2
        className="w-3 h-3 mr-0.5 text-muted-foreground shrink-0"
        aria-hidden
      />
    </Tooltip>
  );
};

/**
 * Component that looks up the tool display name from fetched tools data.
 * Falls back to the provided label if the tool isn't found.
 */
const ToolDisplayName: React.FC<{ toolKey: string; fallbackLabel: string }> = ({
  toolKey,
  fallbackLabel,
}) => {
  const mentionMode = useAgentMentionToolsMode();
  const toolInfo = useToolInfo(toolKey);
  if (mentionMode.mode === 'owner_org' && !mentionMode.isLoading) {
    const row = lookupMentionedTool(mentionMode.toolLookup, toolKey);
    if (row !== null) {
      return <>{row.name}</>;
    }
  }
  return <>{toolInfo?.name ?? fallbackLabel}</>;
};

function parseMentionedResource(
  data: MentionData | undefined,
  referenceId: string | null | undefined
): MentionedResource | undefined {
  const referenceIdTrimmed =
    referenceId !== undefined &&
    referenceId !== null &&
    referenceId.trim() !== ''
      ? referenceId.trim()
      : undefined;

  if (data?.t === 'agent') {
    if (referenceIdTrimmed === undefined) {
      return undefined;
    }
    return {
      resourceType: ResourceType.Agent,
      resourceId: referenceIdTrimmed,
    };
  }

  if (data?.t === 'document') {
    if (referenceIdTrimmed === undefined) {
      return undefined;
    }
    return {
      resourceType: ResourceType.Document,
      resourceId: referenceIdTrimmed,
    };
  }

  if (data?.t !== 'resource') {
    return undefined;
  }

  const inner = data.data;
  if (inner === undefined || inner === null || typeof inner !== 'object') {
    return undefined;
  }
  const resourceType = Reflect.get(inner, 'resourceType');
  const dataResourceId = Reflect.get(inner, 'resourceId');
  const resourceId =
    referenceIdTrimmed ??
    (typeof dataResourceId === 'string' && dataResourceId.trim() !== ''
      ? dataResourceId.trim()
      : undefined);
  if (resourceId === undefined) {
    return undefined;
  }
  if (
    resourceType !== ResourceType.Agent &&
    resourceType !== ResourceType.Document &&
    resourceType !== ResourceType.Dataset
  ) {
    return undefined;
  }
  return {
    resourceType,
    resourceId,
  };
}

const ResourceDisplayName: React.FC<{
  resource: MentionedResource;
  fallbackLabel: string;
}> = ({ resource, fallbackLabel }) => {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  const { data } = useQuery({
    queryKey: queryKeys.resource.get({
      orgId,
      type: resource.resourceType,
      id: resource.resourceId,
    }),
    queryFn: async () =>
      client.resource.get({
        type: resource.resourceType,
        id: resource.resourceId,
      }),
  });

  if (data === undefined || data.isErr()) {
    return <>{fallbackLabel}</>;
  }
  // The mention already carries a label the author typed, so an unnamed
  // resource keeps that rather than falling back to "Untitled …".
  return <>{resourceViewOf(data.value).displayName() ?? fallbackLabel}</>;
};

export const MentionNodeView: React.FC<NodeViewProps> = ({
  node,
  extension,
}) => {
  const id = node.attrs['id'] as string;
  const referenceIdAttr = node.attrs['referenceId'] as
    | string
    | null
    | undefined;
  const referenceId = referenceIdAttr === null ? undefined : referenceIdAttr;
  const label = (node.attrs['label'] as string | undefined) ?? id;
  const dataRaw = node.attrs['data'] as unknown;

  const data = React.useMemo(
    (): MentionData | undefined => parseMentionDataAttr(dataRaw),
    [dataRaw]
  );

  // Try multiple sources for the tool key:
  // 1. referenceId attribute (set by migration to new key like "heyreach/campaigns/addLeads")
  // 2. Nested data.data.def.toolId
  // 3. id attribute (might be the key for new tools)
  const toolIdFromData = React.useMemo(() => {
    if (
      data?.t !== 'tool' ||
      data.data === undefined ||
      data.data === null ||
      typeof data.data !== 'object'
    ) {
      return undefined;
    }
    const def = Reflect.get(data.data, 'def');
    if (def === undefined || def === null || typeof def !== 'object') {
      return undefined;
    }
    const toolId = Reflect.get(def, 'toolId');
    return typeof toolId === 'string' ? toolId : undefined;
  }, [data]);

  const toolKey =
    data?.t === 'tool' ? referenceId ?? toolIdFromData ?? id : undefined;

  const isInputMention = data?.t === 'input';
  const showOwnershipIndicator =
    (
      extension.options as {
        showOwnershipIndicator?: boolean;
        ownershipTooltipContent?: React.ReactNode;
      }
    ).showOwnershipIndicator === true;
  const ownershipTooltipContent =
    (
      extension.options as {
        showOwnershipIndicator?: boolean;
        ownershipTooltipContent?: React.ReactNode;
      }
    ).ownershipTooltipContent ?? 'This mention was added by another user.';

  const handleClick = React.useCallback(() => {
    const onClick = (
      extension.options as {
        onClick?: (
          data: MentionData & {
            label?: string;
            toolKey?: string;
            referenceId?: string;
          }
        ) => void;
      }
    ).onClick;
    if (onClick !== undefined && data !== undefined) {
      onClick({
        ...data,
        label,
        toolKey,
        ...(referenceId !== undefined && { referenceId }),
      });
    }
  }, [extension, data, label, toolKey, referenceId]);

  const color = getMentionBadgeColor(data);
  const resource = React.useMemo(
    () => parseMentionedResource(data, referenceId),
    [data, referenceId]
  );

  return (
    <NodeViewWrapper className="mention-node react-renderer inline">
      <Badge
        variant={color}
        size="sm"
        // The badge is `whitespace-nowrap`, so a long name cannot wrap with the
        // line around it. Without a cap it ran past the editor's edge on a phone
        // and gave the whole document a horizontal scrollbar (COT-7001); capped,
        // the name truncates instead.
        className="cursor-pointer max-w-full"
        onClick={handleClick}
      >
        {toolKey !== undefined && (
          <>
            {showOwnershipIndicator && (
              <MentionOwnershipIndicator
                tooltipContent={ownershipTooltipContent}
              />
            )}
            <OwnerOrgToolIndicator toolKey={toolKey} />
            <ToolConfigurationIndicator toolKey={toolKey} />
          </>
        )}
        {toolKey === undefined && showOwnershipIndicator && (
          <MentionOwnershipIndicator tooltipContent={ownershipTooltipContent} />
        )}
        <span className="min-w-0 truncate">
          @
          {toolKey !== undefined ? (
            <ToolDisplayName toolKey={toolKey} fallbackLabel={label} />
          ) : resource !== undefined ? (
            <ResourceDisplayName resource={resource} fallbackLabel={label} />
          ) : isInputMention ? (
            <span className="font-mono text-xs">{label}</span>
          ) : (
            label
          )}
        </span>
      </Badge>
    </NodeViewWrapper>
  );
};
